import {
  createContext,
  useState
} from 'react'
import {
  erase,
  get,
  set
} from 'browser-cookies'
import type { ReactNode } from 'react'

interface Context {
  selectedMonitorConfigId?: string
  setSelectedMonitorConfigId: (newMonitorConfigId?: string) => void
}

const MonitorConfig = createContext<Context | undefined>(undefined)

const COOKIE_NAME = 'selectedMonitorConfigId'

interface ProviderProps {
  children: ReactNode
  selectedMonitorConfigId?: string // can be used to change the cookie
}

export const MonitorConfigProvider = ({
  children,
  selectedMonitorConfigId: monitorConfigIdProp
}: ProviderProps) => {
  const cookie = get(COOKIE_NAME) || undefined

  const [selectedMonitorConfigId, setSelectedMonitorConfigIdState] = useState<string | undefined>(monitorConfigIdProp || cookie)

  const updateCookie = (newMonitorConfigId?: string) => {
    if (!newMonitorConfigId) {
      erase(COOKIE_NAME)

      return
    }

    set(COOKIE_NAME, newMonitorConfigId)
  }

  if (selectedMonitorConfigId !== cookie) updateCookie(selectedMonitorConfigId)

  const setSelectedMonitorConfigId = (newMonitorConfigId?: string) => {
    updateCookie(newMonitorConfigId)
    setSelectedMonitorConfigIdState(newMonitorConfigId)
  }

  const value = {
    selectedMonitorConfigId,
    setSelectedMonitorConfigId
  }

  return (
    <MonitorConfig.Provider value={value}>
      {children}
    </MonitorConfig.Provider>
  )
}

interface ConsumerProps {
  children: (context: Context) => ReactNode
}

export const MonitorConfigConsumer = ({
  children: renderChildren
}: ConsumerProps) =>
  <MonitorConfig.Consumer>
    {
      (context?: Context) => {
        if (context === undefined) {
          throw new Error('<MonitorConfigConsumer /> must be used within <MonitorConfigProvider />')
        }

        return renderChildren(context)
      }
    }
  </MonitorConfig.Consumer>
